/**
 * LES NUITS — CE QUE LE CALENDRIER, LA FICHE ET L'ACCUEIL PARTAGENT.
 *
 * Le serveur décide de l'état d'une nuit (`packages/core/src/finance/
 * night-status.ts`) ; le navigateur ne fait que le PEINDRE. Aucune règle de
 * délai, de plancher ou de « à faire » n'est recopiée ici : une deuxième copie
 * finirait par contredire la première — un calendrier vert, un récapitulatif
 * du mardi qui réclame la même nuit.
 *
 * ═══ UNE COULEUR NE PORTE JAMAIS L'INFORMATION SEULE ═══
 *
 * Chaque état a son libellé ET son icône : un daltonien, un écran au soleil,
 * une capture en noir et blanc disent la même chose que l'écran en couleur.
 */

import type { AssociationsVue } from './BilletterieAssociations.js'
import type { ReactNode } from 'react'
import {
  BadgeCheck, CheckCircle2, CircleDashed, Clock, HelpCircle, Hourglass, Moon, MoonStar, AlertTriangle, CalendarClock,
  type LucideIcon,
} from 'lucide-react'
import { formatMoney } from './money.js'
import { commeObjet, lireJson, objetSous, tableauSous, type ClosingDetail, type Etat } from './ClosingCommon.js'

// Les phrases des ventes du guichet d'une autre nuit vivent dans un modèle pur
// (testé sans navigateur) ; on les rend aussi d'ici, où les écrans les cherchent.
export { jourMois, phraseConcordance, phraseSousLigneHorsNuit } from './guichet-hors-nuit-model.js'

/* ═══════════════════════ ce que l'API rend ═══════════════════════ */

/** Miroir EXACT de `NightState` côté serveur — un test croisé le verrouille. */
export type NightState =
  | 'A_VENIR' | 'EN_COURS' | 'HORS_SUIVI' | 'FERMEE' | 'RAPPORT_ATTENDU'
  | 'SOIREE_SANS_RAPPORT' | 'A_CLOTURER' | 'EN_RETARD' | 'CLOTUREE' | 'VALIDEE'

export interface NightSoiree { id: string; name: string; starts_at: string; status: 'SCHEDULED' | 'CANCELLED' }

/**
 * LES VENTES DU GUICHET FAITES PENDANT UNE AUTRE NUIT (08/10/2026), vues
 * d'une nuit : `VENTE` = vendues pendant cette nuit sur la soirée d'une
 * autre (l'argent est dans la caisse d'ici) ; `SOIREE` = la soirée d'ici,
 * vendue pendant une autre nuit. La phrase vient du serveur, sans montant ;
 * les montants valent `null` sans le droit de lire la billetterie — jamais 0.
 */
/** `SANS_OBJET` : les billets ne sont plus hors nuit (soirée redatée après la décision), rien à décider. */
export type StatutHorsNuit = 'EN_ATTENTE' | 'RATTACHE' | 'MAINTENU' | 'SANS_OBJET'

export interface OffNightSaleRow {
  key: string
  side: 'VENTE' | 'SOIREE'
  /** L'autre nuit du cas : celle de la soirée (VENTE) ou celle de la vente (SOIREE). */
  other_business_date: string
  from_event_id: string
  from_event_name: string
  from_business_date: string
  sale_business_date: string
  classe: string
  classe_label: string
  /** Les soirées maintenues de la nuit de vente : la cible d'un rattachement. */
  to_candidates?: Array<{ event_id: string; name: string }>
  /** Vu de la nuit de la soirée, en attente : la source Accueil posée les contient déjà. */
  source_contains?: boolean | null
  /** Combien (TPE + espèces) cette source en contient — une part seulement si la caisse a revendu après la pose. */
  source_contains_cents?: number | null
  /**
   * Ce que la décision en vigueur fait à l'attendu de l'Accueil de CETTE nuit
   * (TPE + espèces), calculé par le serveur comme ses transferts — y compris
   * quand des billets vendus depuis remettent le cas « en attente ». `null`
   * sans le droit de lire la billetterie.
   */
  expected_effect_cents?: number | null
  status: StatutHorsNuit
  status_label: string
  /** En attente, de l'argent en jeu : la finalisation des deux nuits l'attend. */
  blocking: boolean
  tickets: number
  paid_tickets: number
  free_tickets: number
  scanned_tickets: number
  undecided_tickets: number
  card_present_cents: number | null
  cash_cents: number | null
  total_cents: number | null
  decision: {
    id: string; decision: 'RATTACHER' | 'MAINTENIR'; created_at: string; created_by_name: string | null
    reason: string | null; from_source_included: boolean; total_cents: number | null; tickets: number
    /** TPE + espèces que la source de la nuit de la soirée contenait, figés à la décision ; `null` sans le droit. */
    included_cents?: number | null
  } | null
  /** Une clôture finalisée de CETTE nuit que la décision rend fausse. */
  revision: {
    business_date: string; closing_id: string
    effect: 'CA_SUREVALUE' | 'CA_SOUS_EVALUE' | 'ENTREES_SEULEMENT' | 'A_VERIFIER'
    ca_delta_cents: number | null; entries_delta: number; message: string
  } | null
  sentence: string
}

export interface NightRow {
  business_date: string
  state: NightState
  state_label: string
  todo: boolean
  flags: string[]
  flag_labels: string[]
  soirees: NightSoiree[]
  zap: {
    principal_count: number
    complement_count: number
    annex_count: number
    ignored_count: number
    /** `null` = inconnu OU non autorisé : jamais un zéro. */
    announced_cents: number | null
    offers_cents: number | null
  }
  shotgun_report_count: number
  closing: {
    id: string; status: 'DRAFT' | 'READY' | 'CLOSED'; revision: number; version: number; closed_at: string | null
    /** Qui a écrit en dernier dans la clôture, et quand. */
    last_editor_name: string | null
    last_edited_at: string | null
  } | null
  /** Nuit validée sans clôture détaillée (chiffre d'affaires et entrées arrêtés en bloc). */
  validation: {
    revenue_cents: number | null; entries: number | null
    validated_by_name: string | null; validated_at: string; reason: string | null
    /** Nuit reprise de l'historique (`reprise-historique.ts`) ; absent d'un serveur d'avant le 16/10/2026. */
    history?: unknown
  } | null
  /** Absent d'un serveur d'avant le 08/10/2026 : lu comme « aucun cas ». */
  off_night_sales?: OffNightSaleRow[]
}

export interface NightsWeekRow {
  week_start: string
  nights_todo: number
  zap_announced_cents: number | null
  zap_offers_cents: number | null
  complete: boolean
}

export interface NightsResponse {
  current_night: string
  nights: NightRow[]
  weeks: NightsWeekRow[]
  oldest_pending: NightRow | null
}

export interface NightReportRow {
  document_id: string
  filename: string
  received_at: string
  role: 'PRINCIPAL' | 'COMPLEMENT'
  period_start: string | null
  period_end: string | null
  reading_status: 'PARSED' | 'NEEDS_REVIEW' | 'FAILED' | null
  announced_cents: number | null
  offers_cents: number | null
  duplicate_of: string | null
  /** Les nuits que la période couvre (une d'ordinaire) — le serveur les calcule. */
  nights_covered?: string[]
}

export interface NightPosRow {
  point_of_sale_id: string | null
  name: string
  kind: string | null
  resolved: boolean
  by_method: Record<string, number>
  announced_cents: number
  offers_cents: number
}

export interface NightDocumentRow {
  id: string; filename: string; provider: string; report_type: string
  parse_status: string; ignored: boolean; ignored_reason: string | null; received_at: string
}

/** Le guichet Shotgun de la nuit — la caisse d'entrée. Valeur FACIALE, à comparer. */
export interface NightOnsiteRow {
  tickets: number; facial_cents: number; unknown_cents: number
  free_tickets: number; unpriced_tickets: number
  coverage: 'COMPLETE' | 'PARTIAL' | 'NONE'
  by_method: {
    card_present_cents: number; cash_cents: number; card_online_cents: number
    wallet_cents: number; other_cents: number
  }
  caveats: string[]
}

export interface NightAccueilRow {
  read_through: string | null
  settled: boolean
  /** `null` = ni soirée ni guichet, ou pas le droit de lire la billetterie. */
  onsite: NightOnsiteRow | null
}

/** Une reddition Shotgun : un CONTRÔLE, jamais une ligne de clôture. */
export interface NightRedditionRow {
  document_id: string; filename: string; ignored: boolean; ignored_reason: string | null; parse_status: string
  report_id: string | null; issued_on: string | null; event_name: string | null
  onsite_ttc_cents: number | null; onsite_tickets: number | null
  online_ttc_cents: number | null; sales_ttc_cents: number | null
  fees_ttc_cents: number | null; balance_cents: number | null
  /** Doublon ou version remplacée : la référence de celle qui compte à sa place. Absent d'un serveur d'avant le 11/10/2026. */
  set_aside_by?: string | null
}

export interface NightRedditionControlRow {
  reddition_onsite_cents: number | null
  billetterie_onsite_cents: number | null
  ecart_billetterie_cents: number | null
  comptage_accueil_cents: number | null
  ecart_comptage_cents: number | null
  /** Vendus pendant cette nuit sur la soirée d'une autre : dans la caisse, dans aucune reddition d'ici. */
  hors_nuit_entrant_cents?: number | null
  /** La soirée d'ici, vendue pendant une autre nuit. */
  hors_nuit_sortant_cents?: number | null
  /** comptage − (reddition + ventes entrantes). */
  ecart_comptage_net_cents?: number | null
}

/**
 * Les préventes en ligne de la nuit : la reddition, sinon les billets — la
 * source est dite. `ttc_cents` : ce que les acheteurs ont payé, prix + frais ;
 * `payout_cents` : ce que Shotgun reverse au club, la part du chiffre
 * d'affaires (prix + 1 € par billet). `null` = non lisible ou masqué ; absent
 * d'un serveur d'avant le 11/10/2026 = « non servi ».
 */
export interface NightPresalesRow {
  ttc_cents: number | null
  payout_cents?: number | null
  source: 'REDDITION' | 'BILLETS' | 'REDDITION_ET_BILLETS' | 'SANS_BILLET' | null
  tickets: number
  reddition_refs: string[]
  provisional: boolean
  notes: string[]
}

export interface NightDetailResponse extends NightRow {
  /** Absent d'un serveur d'avant octobre 2026 : « non servi », jamais 0. */
  preventes?: NightPresalesRow | null
  /** La billetterie des associations (Pretix), 16/10/2026 ; absente d'un serveur d'avant. */
  associations?: AssociationsVue | null
  reports: NightReportRow[]
  by_point_of_sale: NightPosRow[]
  unresolved_registers: string[]
  documents: NightDocumentRow[]
  accueil: NightAccueilRow
  redditions: NightRedditionRow[]
  controle_reddition: NightRedditionControlRow | null
}


/** Garde minimale : la forme est celle d'une réponse « nuits », sinon on le dit. */
export function commeNuits(v: unknown): NightsResponse | null {
  const o = objetSous(v)
  const nuits = tableauSous(v, 'nights')
  if (!o || !nuits || typeof o['current_night'] !== 'string') return null
  return o as unknown as NightsResponse
}

export function commeNuit(v: unknown): NightDetailResponse | null {
  const o = objetSous(v)
  if (!o || typeof o['business_date'] !== 'string' || typeof o['state'] !== 'string') return null
  return o as unknown as NightDetailResponse
}

/* ═══════════════════════ ce que la fiche d'une nuit lit pour ses chiffres ═══════════════════════ */

/*
 * Les deux lectures dont la fiche d'une nuit tire son bandeau — « Rapport »,
 * « Compté », « Chiffre d'affaires » — et que les cartes de l'accueil refont
 * à l'identique (10/10/2026) : la même route, la même garde, pour que la
 * carte et la fiche ne lisent jamais deux choses différentes.
 */

/**
 * La fiche d'une nuit (`GET /finance/nights/:date`) : rapports, guichet, préventes, validation.
 * `signal` : la carte démontée annule sa lecture sur le réseau.
 */
export function lireFicheNuit(date: string, signal?: AbortSignal): Promise<Etat<NightDetailResponse>> {
  return lireJson<NightDetailResponse>(`/api/v1/finance/nights/${date}`, commeNuit, signal)
}

/**
 * La clôture d'une nuit (`GET /finance/closings/:id`) : le compté et les
 * coupures n'existent que là. Les appelants ne la lisent qu'avec le droit de
 * lire les montants (`finance.source.read`), comme la fiche.
 */
export function lireClotureDeNuit(id: string, signal?: AbortSignal): Promise<Etat<ClosingDetail>> {
  return lireJson<ClosingDetail>(`/api/v1/finance/closings/${id}`, commeObjet(['closing', 'detail'], 'business_date'), signal)
}

/* ═══════════════════════ peindre un état ═══════════════════════ */

/** La teinte, au sens du design system (`--status-*`). Elle ACCOMPAGNE le texte. */
export type NightTone = 'idle' | 'neutral' | 'info' | 'warning' | 'error' | 'ok'

interface Peinture { tone: NightTone; icon: LucideIcon; court: string }

/** Le libellé COURT pour une case de calendrier ; le libellé long vient du serveur. */
export const NIGHT_PAINT: Record<NightState, Peinture> = {
  A_VENIR: { tone: 'neutral', icon: CalendarClock, court: 'À venir' },
  EN_COURS: { tone: 'info', icon: MoonStar, court: 'En cours' },
  HORS_SUIVI: { tone: 'idle', icon: CircleDashed, court: 'Hors suivi' },
  FERMEE: { tone: 'idle', icon: Moon, court: 'Fermé' },
  RAPPORT_ATTENDU: { tone: 'neutral', icon: Hourglass, court: 'Rapport attendu' },
  SOIREE_SANS_RAPPORT: { tone: 'error', icon: HelpCircle, court: 'Sans rapport ZAP' },
  A_CLOTURER: { tone: 'warning', icon: Clock, court: 'À clôturer' },
  EN_RETARD: { tone: 'error', icon: AlertTriangle, court: 'En retard' },
  CLOTUREE: { tone: 'ok', icon: CheckCircle2, court: 'Clôturée' },
  VALIDEE: { tone: 'ok', icon: BadgeCheck, court: 'Validée' },
}

/** La pastille du design system pour un état — même texte, même icône partout. */
export function EtatNuit(props: { state: NightState; libelle?: string; court?: boolean }): ReactNode {
  const p = NIGHT_PAINT[props.state]
  const Icone = p.icon
  return (
    <span className={`fn-etat fn-etat--${p.tone}`}>
      <Icone size={14} aria-hidden="true" />
      <span>{props.court ? p.court : (props.libelle ?? p.court)}</span>
    </span>
  )
}

/** Un montant de nuit : « — » quand il est inconnu ou non autorisé, jamais 0 €. */
export function montantNuit(cents: number | null | undefined): string {
  return typeof cents === 'number' ? formatMoney(cents) : '—'
}

/* ═══════════════════════ la grille d'un mois ═══════════════════════ */

/*
 * De l'arithmétique de CALENDRIER, pas un classement d'instants : on ne
 * fabrique ici que des dates AAAA-MM-JJ, à midi UTC pour qu'aucun fuseau ni
 * changement d'heure ne fasse glisser un jour. QUELLE nuit est la nuit
 * courante, et dans quel état elle est, reste l'affaire du serveur.
 */
const MIDI = 'T12:00:00Z'
const versDate = (ymd: string): Date => new Date(`${ymd}${MIDI}`)
const versYmd = (d: Date): string => d.toISOString().slice(0, 10)

export function decaler(ymd: string, jours: number): string {
  const d = versDate(ymd)
  d.setUTCDate(d.getUTCDate() + jours)
  return versYmd(d)
}

/** Lundi de la semaine d'une date. */
export function lundiDe(ymd: string): string {
  const jour = versDate(ymd).getUTCDay() // 0 = dimanche
  return decaler(ymd, jour === 0 ? -6 : 1 - jour)
}

/** Premier jour du mois d'une date, au format AAAA-MM-01. */
export function debutDeMois(ymd: string): string {
  return `${ymd.slice(0, 7)}-01`
}

export function moisSuivant(mois01: string, n: number): string {
  const d = versDate(mois01)
  d.setUTCMonth(d.getUTCMonth() + n, 1)
  return versYmd(d)
}

/** Les semaines complètes (lundi → dimanche) qui couvrent un mois. */
export function semainesDuMois(mois01: string): string[][] {
  const premier = lundiDe(mois01)
  const dernierDuMois = decaler(moisSuivant(mois01, 1), -1)
  const semaines: string[][] = []
  for (let lundi = premier; lundi <= dernierDuMois; lundi = decaler(lundi, 7)) {
    semaines.push(Array.from({ length: 7 }, (_, i) => decaler(lundi, i)))
  }
  return semaines
}

const MOIS = new Intl.DateTimeFormat('fr-FR', { month: 'long', year: 'numeric', timeZone: 'UTC' })
const JOUR_COURT = new Intl.DateTimeFormat('fr-FR', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' })

export function libelleMois(mois01: string): string { return MOIS.format(versDate(mois01)) }
export function jourCourt(ymd: string): string { return JOUR_COURT.format(versDate(ymd)) }
export function numeroDuJour(ymd: string): number { return Number(ymd.slice(8, 10)) }

export const JOURS_SEMAINE = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'] as const

/* ═══════════════════════ l'adresse d'une clôture ═══════════════════════ */

/**
 * « THE CULTURE-ENTRE NOUS : SYLAAR… » → « the-culture-entre-nous-sylaar ».
 * Décoratif : seule la DATE identifie la clôture ; le nom aide à la lire.
 */
export function slugSoiree(nom: string): string {
  const s = nom.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()
    .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
  if (s.length <= 32) return s
  const coupe = s.slice(0, 33)
  return coupe.slice(0, Math.max(coupe.lastIndexOf('-'), 16)).replace(/-+$/g, '')
}

/**
 * L'ADRESSE D'UNE CLÔTURE : `/comptabilite/clotures/2026-09-26-the-culture-…`.
 * Une nuit n'a qu'une clôture courante : la date suffit à la retrouver, le nom
 * de la soirée rend le lien lisible dans un message ou un historique. Les
 * anciens liens par identifiant restent valables (l'assistant les réécrit).
 */
export function urlCloture(date: string, soirees?: Array<{ name: string; status?: string }> | null): string {
  const s = (soirees ?? []).find((x) => x.status !== 'CANCELLED') ?? null
  const slug = s ? slugSoiree(s.name) : ''
  return `/comptabilite/clotures/${date}${slug ? `-${slug}` : ''}`
}

/** La nuit d'une référence d'adresse (« 2026-09-26-… »), ou `null` pour un identifiant. */
export function nuitDeReference(ref: string): string | null {
  const m = /^(\d{4}-\d{2}-\d{2})(?:-[a-z0-9-]*)?$/.exec(ref)
  return m ? m[1]! : null
}
